import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { BiArrowBack } from "react-icons/bi";
import { toast } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";

import {
  getInvoiceableJr,
  getInvoiceDetail,
  generateInvoice,
  resetGenerated,
  invoiceV2Selector,
} from "./invoiceV2Slice";
import { getMinKlien, klienSelector } from "../../master/klien/klienSlice";
import { Colors } from "../../../assets/Colors";

function fmt(n) {
  if (!n && n !== 0) return "0";
  return new Intl.NumberFormat("id-ID").format(n);
}

export default function InvoiceGeneratePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const invV2 = useSelector(invoiceV2Selector);
  const klien = useSelector(klienSelector);

  // Dibuka dari tombol "Cetak Invoice" di daftar Quotation: klien & No. QO
  // sudah ditentukan, jadi keduanya jadi nilai awal dan daftar JR langsung
  // menyusut ke quotation itu saja. Dibuka langsung dari menu → keduanya "".
  const awalKlienID = location.state?.KlienID ?? "";
  const awalQoNo    = location.state?.QoNo ?? "";
  // Dibuka dari tombol "Revisi" di detail invoice: JR invoice itu langsung terpilih.
  const awalReviseInvNo = location.state?.ReviseInvNo ?? "";
  const preselectDoneRef = useRef(false);

  const [klienID, setKlienID]       = useState(awalKlienID);
  const [qoNo, setQoNo]             = useState(awalQoNo); // "" = semua quotation
  const [cqKey, setCqKey]           = useState("");       // "" = semua CQ; "0" = tanpa CQ; lainnya = CQID
  const [selected, setSelected]     = useState({}); // { JrID: true }
  const [invoiceDate, setInvoiceDate] = useState(new Date());
  const [dueDate, setDueDate]       = useState(new Date(Date.now() + 30 * 86400000));
  const [taxPct, setTaxPct]         = useState(11);
  const [description, setDescription] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [dpNumber, setDpNumber]     = useState("");
  const [qtyRounding, setQtyRounding] = useState("decimal"); // decimal | round
  const wasReviseRef = useRef(false);

  useEffect(() => {
    dispatch(getMinKlien({ search: "" }));
    // Muat langsung untuk klien asal (kalau ada) supaya daftar JR tidak
    // sempat menampilkan seluruh klien sekejap sebelum tersaring.
    dispatch(getInvoiceableJr(awalKlienID));
    return () => { dispatch(resetGenerated()); };
  }, []); // eslint-disable-line

  // Navigate after generate
  useEffect(() => {
    if (invV2.generatedInvNo) {
      toast.success(wasReviseRef.current
        ? "Invoice berhasil direvisi: " + invV2.generatedInvNo
        : "Invoice berhasil dibuat: " + invV2.generatedInvNo);
      const no = invV2.generatedInvNo;
      dispatch(resetGenerated());
      navigate("/invoice-detail-v2", { state: { InvNo: no } });
    }
  }, [invV2.generatedInvNo]); // eslint-disable-line

  useEffect(() => {
    if (invV2.error) { toast.error(invV2.error); dispatch(resetGenerated()); }
  }, [invV2.error]); // eslint-disable-line

  const klienOptions = klien.minData ?? [];

  const handleKlienChange = (val) => {
    setKlienID(val);
    setQoNo("");
    setCqKey("");
    setSelected({});
    dispatch(getInvoiceableJr(val));
  };

  const handleQoChange = (val) => {
    setQoNo(val);
    setCqKey("");
    setSelected({});
  };

  const handleCqChange = (val) => {
    setCqKey(val);
    setSelected({});
  };

  const jrList = invV2.invoiceable ?? [];

  useEffect(() => {
    if (!awalReviseInvNo || preselectDoneRef.current || !jrList.length) return;
    const jrs = jrList.filter(j => j.ActiveInvNo === awalReviseInvNo);
    if (jrs.length) {
      preselectDoneRef.current = true;
      setSelected(Object.fromEntries(jrs.map(j => [j.JrID, true])));
    }
  }, [jrList]); // eslint-disable-line

  // Opsi No. Quotation diturunkan dari JR yang siap ditagih
  const qoOptions = useMemo(() => {
    const uniq = [...new Set(jrList.map(j => j.QoNo).filter(Boolean))];
    // QO asal (dari tombol Cetak Invoice) selalu disertakan walau belum punya
    // JR approved — kalau tidak, dropdown tampak kosong padahal filternya aktif
    // dan user mengira halamannya rusak, bukan "QO ini belum ada yang bisa ditagih".
    if (awalQoNo && !uniq.includes(awalQoNo)) uniq.unshift(awalQoNo);
    return [{ value: "", label: "Semua Quotation" }, ...uniq.map(q => ({ value: q, label: q }))];
  }, [jrList, awalQoNo]);

  // Kunci kelompok invoice: satu invoice = satu customer + satu CQ (JR tanpa CQ
  // = kelompok tersendiri). Backend menolak campuran; UI mencegahnya lebih awal.
  const groupOf = (j) => `${j.KlienID}|${j.CQID || 0}`;
  const cqLabel = (j) => j.CQID ? (j.NamaCQ ? `${j.KodeCQ} — ${j.NamaCQ}` : j.KodeCQ) : "Tanpa CQ";

  const byQo = qoNo ? jrList.filter(j => j.QoNo === qoNo) : jrList;

  // Opsi CQ diturunkan dari JR (setelah filter QO) yang siap ditagih
  const cqOptions = useMemo(() => {
    const m = new Map();
    byQo.forEach(j => { const k = String(j.CQID || 0); if (!m.has(k)) m.set(k, cqLabel(j)); });
    return [{ value: "", label: "Semua CQ" },
      ...[...m].map(([value, label]) => ({ value, label }))
        .sort((a, b) => (a.value === "0") - (b.value === "0") || a.label.localeCompare(b.label))];
  }, [byQo]); // eslint-disable-line

  // Daftar yang ditampilkan: difilter berdasarkan No. Quotation & CQ terpilih
  const displayList = cqKey === "" ? byQo : byQo.filter(j => String(j.CQID || 0) === cqKey);

  // JR yang sudah dicetak BISA dipilih lagi = REVISI invoice aktifnya (nomor tetap,
  // isi dihitung ulang, versi lama diarsipkan di backend). Bukan invoice kedua.
  // Invoice berstatus Lunas tidak bisa direvisi.
  const invoicedCount = displayList.filter(j => j.ActiveInvNo).length;

  // Pilihan dihitung dari seluruh jrList: saat merevisi, semua JR invoice itu ikut
  // terpilih walau tersembunyi oleh filter QO/CQ (kalau tidak, JR itu terlepas).
  const selectedJrs = jrList.filter(j => selected[j.JrID]);
  const activeGroup = selectedJrs.length ? groupOf(selectedJrs[0]) : null;
  const reviseInvNo = selectedJrs.find(j => j.ActiveInvNo)?.ActiveInvNo || "";
  const isPaid = (j) => j.ActiveInvStatus === "paid";

  // Masuk mode revisi → isi form dengan pengaturan invoice yang direvisi.
  useEffect(() => {
    if (!reviseInvNo) return;
    dispatch(getInvoiceDetail(reviseInvNo)).unwrap().then((res) => {
      const d = res?.data;
      if (!d) return;
      if (d.InvoiceDate) setInvoiceDate(new Date(d.InvoiceDate));
      if (d.DueDate) setDueDate(new Date(d.DueDate));
      setTaxPct(d.TaxPct ?? 11);
      setDescription(d.Description || "");
      setBankAccount(d.BankAccount || "");
      setDpNumber(d.DPNumber || "");
      setQtyRounding(d.QtyRounding || "decimal");
    }).catch(() => {});
  }, [reviseInvNo]); // eslint-disable-line

  const toggle = (jrID) => {
    const jr = jrList.find(j => j.JrID === jrID);
    if (!jr) return;
    if (isPaid(jr)) {
      toast.info(`JR ini ditagih di ${jr.ActiveInvNo} yang sudah Lunas — tidak bisa direvisi.`);
      return;
    }
    if (!selected[jrID] && activeGroup && groupOf(jr) !== activeGroup) {
      toast.error(`Satu invoice hanya untuk satu customer & CQ. JR ini ${cqLabel(jr)}, sedangkan yang terpilih ${cqLabel(selectedJrs[0])}.`);
      return;
    }
    if (!selected[jrID] && jr.ActiveInvNo) {
      if (reviseInvNo && reviseInvNo !== jr.ActiveInvNo) {
        toast.error(`Sedang merevisi ${reviseInvNo}. JR ini milik ${jr.ActiveInvNo} — revisi satu invoice per kali.`);
        return;
      }
      // Pilih sekaligus semua JR di invoice itu, supaya revisi tidak tak sengaja
      // melepas JR lain. JR yang memang mau dikeluarkan bisa di-uncheck setelahnya.
      const siblings = jrList.filter(j => j.ActiveInvNo === jr.ActiveInvNo);
      setSelected(s => ({ ...s, ...Object.fromEntries(siblings.map(j => [j.JrID, true])) }));
      if (siblings.length > 1) toast.info(`${siblings.length} JR di ${jr.ActiveInvNo} ikut terpilih.`);
      return;
    }
    setSelected(s => ({ ...s, [jrID]: !s[jrID] }));
  };
  // "Pilih semua" hanya JR yang belum ditagih (revisi dipilih per invoice).
  const selectable = displayList.filter(j => !j.ActiveInvNo);
  const displayGroups = new Set(selectable.map(groupOf));
  const toggleAll = () => {
    if (selectable.length > 0 && selectable.every(j => selected[j.JrID])) {
      setSelected(s => Object.fromEntries(Object.entries(s).filter(([id]) => !selectable.some(j => j.JrID === id))));
      return;
    }
    if (displayGroups.size > 1 || (activeGroup && [...displayGroups].some(g => g !== activeGroup))) {
      toast.error("Daftar berisi beberapa customer/CQ — pilih filter Klien & CQ dahulu untuk memilih semua.");
      return;
    }
    setSelected(s => ({ ...s, ...Object.fromEntries(selectable.map(j => [j.JrID, true])) }));
  };
  // Nilai per JR mengikuti opsi pembulatan; JR yang sedang direvisi memakai semua
  // line approved-nya (bukan hanya yang belum ditagih).
  const billedOf = (j) => j.ActiveInvNo
    ? (qtyRounding === "round" ? j.TotalAllRound : j.TotalAll)
    : (qtyRounding === "round" ? j.TotalBilledRound : j.TotalBilled);
  const subtotal = selectedJrs.reduce((s, j) => s + (billedOf(j) || 0), 0);
  const taxAmount = subtotal * (parseFloat(taxPct) || 0) / 100;
  const total = subtotal + taxAmount;

  const handleGenerate = () => {
    if (selectedJrs.length === 0) { toast.error("Pilih minimal satu JR"); return; }
    if (reviseInvNo) {
      const ok = window.confirm(
        `Revisi invoice ${reviseInvNo}?\n\n` +
        `• Nomor invoice tetap, isinya dihitung ulang dari JR terpilih.\n` +
        `• Versi sebelumnya disimpan sebagai arsip.\n` +
        `• JR invoice ini yang tidak dipilih akan dilepas (bisa ditagih lagi).`);
      if (!ok) return;
    }
    wasReviseRef.current = !!reviseInvNo;
    dispatch(generateInvoice({
      KlienID: klienID,
      JrIDs: selectedJrs.map(j => j.JrID),
      DPNumber: dpNumber,
      InvoiceDate: invoiceDate.toISOString().slice(0, 10),
      DueDate: dueDate.toISOString().slice(0, 10),
      TaxPct: parseFloat(taxPct) || 0,
      Description: description,
      BankAccount: bankAccount,
      QtyRounding: qtyRounding,
    }));
  };

  return (
    <div className="md:ml-64 px-4 md:px-6 pb-20">
      <div className="flex items-center justify-between py-4 mb-2">
        <button onClick={() => navigate("/invoice-list-v2")}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <BiArrowBack className="text-lg" /> Kembali
        </button>
        <button onClick={handleGenerate} disabled={invV2.isGenerating || selectedJrs.length === 0}
          className="px-5 py-1.5 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
          style={{ background: Colors.primary }}>
          {invV2.isGenerating ? "Memproses…"
            : reviseInvNo ? `Revisi ${reviseInvNo} (${selectedJrs.length} JR)`
            : `Buat Invoice (${selectedJrs.length} JR)`}
        </button>
      </div>

      {/* Header form */}
      <div className="bg-white rounded-xl border p-5 mb-4">
        <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-4">Buat Invoice</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">Filter Klien</label>
            {/* Dibuka dari tombol Cetak Invoice pada sebuah quotation: kliennya
                sudah ditentukan quotation itu, jadi dikunci. Mengizinkan ganti
                klien di sini akan menghasilkan daftar JR yang tak berhubungan
                dengan quotation asal. Dibuka dari menu → tetap bisa dipilih. */}
            <Select menuPortalTarget={document.body}
              isDisabled={!!awalKlienID}
              value={klienOptions.filter(o => o.value === klienID)}
              onChange={e => handleKlienChange(e.value)}
              options={[{ value: "", label: "Semua Klien" }, ...klienOptions]}
              placeholder="Semua klien" />
            {awalKlienID && (
              <p className="mt-1 text-xs text-gray-500">
                Terkunci mengikuti quotation asal.
              </p>
            )}
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">No. Quotation</label>
            <Select menuPortalTarget={document.body}
              value={qoOptions.filter(o => o.value === qoNo)}
              onChange={e => handleQoChange(e.value)}
              options={qoOptions}
              placeholder="Semua quotation" />
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">CQ</label>
            <Select menuPortalTarget={document.body}
              isSearchable
              value={cqOptions.filter(o => o.value === cqKey)}
              onChange={e => handleCqChange(e.value)}
              options={cqOptions}
              placeholder="Semua CQ" />
            <p className="mt-1 text-xs text-gray-500">Satu invoice hanya untuk satu CQ.</p>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">Tanggal Invoice</label>
            <DatePicker dateFormat="dd/MM/yyyy" selected={invoiceDate} onChange={setInvoiceDate}
              className="w-full border rounded px-3 py-2 text-sm outline-none" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">Jatuh Tempo</label>
            <DatePicker dateFormat="dd/MM/yyyy" selected={dueDate} onChange={setDueDate}
              className="w-full border rounded px-3 py-2 text-sm outline-none" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">PPN (%)</label>
            <input type="number" min="0" value={taxPct} onChange={e => setTaxPct(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm outline-none" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">DP Number</label>
            <input type="text" value={dpNumber} onChange={e => setDpNumber(e.target.value)}
              placeholder="No. DP (opsional)"
              className="w-full border rounded px-3 py-2 text-sm outline-none" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">No. Rekening Bank</label>
            <input type="text" value={bankAccount} onChange={e => setBankAccount(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm outline-none" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">Keterangan</label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm outline-none" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">Qty pada Invoice</label>
            <div className="inline-flex w-full rounded border overflow-hidden">
              {[["decimal", "Desimal"], ["round", "Pembulatan"]].map(([v, label]) => (
                <button key={v} type="button" onClick={() => setQtyRounding(v)}
                  className={`flex-1 px-3 py-2 text-sm font-medium ${qtyRounding === v ? "text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                  style={qtyRounding === v ? { background: Colors.primary } : undefined}>
                  {label}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {qtyRounding === "round"
                ? "Qty dibulatkan ke bilangan bulat terdekat (1,4 → 1; 1,5 → 2); nilai ikut qty bulat."
                : "Qty ditagih sesuai input (sampai 4 angka di belakang koma)."}
            </p>
          </div>
        </div>
        {reviseInvNo && (
          <div className="mt-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
            <strong>Mode revisi — {reviseInvNo}.</strong> Nomor invoice tetap; isi dihitung ulang dari JR terpilih
            dan pengaturan di atas. Versi sebelumnya diarsipkan, nomor Revisi naik saat dicetak.
          </div>
        )}
      </div>

      {/* JR selection */}
      <div className="bg-white rounded-xl border overflow-hidden mb-4">
        <div className="px-5 py-3 border-b flex items-center justify-between"
          style={{ background: Colors.primary + "15" }}>
          <span className="font-semibold text-sm" style={{ color: Colors.primary }}>
            Job Request Siap Ditagih (Approved)
          </span>
          <span className="text-xs text-gray-500">
            {displayList.length - invoicedCount} belum dicetak
            {invoicedCount > 0 ? ` · ${invoicedCount} sudah dicetak` : ""}
            {qoNo ? ` · QO ${qoNo}` : ""}
            {activeGroup ? ` · terpilih: ${cqLabel(selectedJrs[0])}` : ""}
            {reviseInvNo ? ` · revisi ${reviseInvNo}` : ""}
          </span>
        </div>

        {invV2.isLoading ? (
          <div className="py-12 text-center text-gray-400 text-sm">Memuat…</div>
        ) : displayList.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">
            {qoNo ? "Tidak ada JR approved untuk quotation ini" : "Tidak ada JR approved"}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-5 py-2.5 w-10">
                  <input type="checkbox"
                    checked={selectable.length > 0 && selectable.every(j => selected[j.JrID])}
                    onChange={toggleAll} />
                </th>
                <th className="px-3 py-2.5 text-xs font-semibold text-gray-500">No. JR</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-gray-500">No. QO</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-gray-500">CQ</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-gray-500">Tgl Kerja</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-gray-500">SPK</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 text-center">Status</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 text-center">Item</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 text-right">Total Billed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayList.map(j => {
                const done = !!j.ActiveInvNo;
                const paid = isPaid(j);
                // JR dari customer/CQ lain dari pilihan aktif diredupkan (tak bisa digabung)
                const otherGroup = activeGroup && groupOf(j) !== activeGroup;
                return (
                <tr key={j.JrID}
                  className={`${paid ? "bg-gray-50 text-gray-400 cursor-not-allowed" : "hover:bg-orange-50 cursor-pointer"} ${selected[j.JrID] ? (done ? "bg-amber-50" : "bg-orange-50") : ""} ${otherGroup ? "opacity-40" : ""}`}
                  onClick={() => toggle(j.JrID)}>
                  <td className="px-5 py-2.5" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" className="w-4 h-4 cursor-pointer"
                      checked={!!selected[j.JrID]} disabled={paid}
                      title={done ? (paid ? `Ditagih di ${j.ActiveInvNo} (Lunas)` : `Pilih untuk merevisi ${j.ActiveInvNo}`) : undefined}
                      onChange={() => toggle(j.JrID)} />
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-gray-700">{j.JrID}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-gray-600">{j.QoNo}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-700 max-w-[200px] truncate" title={cqLabel(j)}>
                    {j.CQID ? (
                      <>
                        <span className="px-1.5 py-0.5 rounded font-semibold bg-blue-50 text-blue-700">{j.KodeCQ}</span>
                        {j.NamaCQ && <span className="ml-1.5 text-gray-600">{j.NamaCQ}</span>}
                      </>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-gray-700">{j.WorkDate}</td>
                  <td className="px-3 py-2.5 text-gray-600">{j.SPKNo || "—"}</td>
                  <td className="px-3 py-2.5 text-center">
                    {paid ? (
                      <span title={`Ditagih di ${j.ActiveInvNo} — sudah Lunas`}
                        className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-200 text-gray-600">
                        Lunas
                      </span>
                    ) : done ? (
                      <span title={`Sudah dicetak di ${j.ActiveInvNo} — pilih untuk merevisi`}
                        className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-700">
                        Sudah dicetak
                      </span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-orange-100 text-orange-700">
                        Belum
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center text-gray-600">{j.ItemCount}</td>
                  <td className="px-3 py-2.5 text-right font-medium text-gray-800">
                    Rp {fmt(billedOf(j))}
                    {done && (
                      <span className="block text-[10px] font-normal text-gray-400">{j.ActiveInvNo}</span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Summary */}
      {selectedJrs.length > 0 && (
        <div className="sticky bottom-0 bg-white rounded-xl border shadow-lg px-6 py-4">
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Subtotal ({selectedJrs.length} JR{qtyRounding === "round" ? ", qty dibulatkan" : ""})</span>
            <span>Rp {fmt(subtotal)}</span>
          </div>
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>PPN {taxPct}%</span>
            <span>Rp {fmt(taxAmount)}</span>
          </div>
          <div className="flex justify-between text-lg font-bold border-t pt-2 mt-1" style={{ color: Colors.primary }}>
            <span>Total</span>
            <span>Rp {fmt(total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
